import { createReducer } from 'redux-create-reducer';
import callAPI from '../../../utils/callAPI';

// Get Notifications
export const ACTION_GET_NOTIFICATIONS = 'ACTION_GET_NOTIFICATIONS';
export const REQUEST_GET_NOTIFICATIONS = 'REQUEST_GET_NOTIFICATIONS';
export const REQUEST_GET_NOTIFICATIONS_SUCCESS = 'REQUEST_GET_NOTIFICATIONS_SUCCESS';
export const REQUEST_GET_NOTIFICATIONS_FAILURE = 'REQUEST_GET_NOTIFICATIONS_FAILURE';

// Mark as Read
export const ACTION_MARK_NOTIFICATION_READ = 'ACTION_MARK_NOTIFICATION_READ';
export const REQUEST_MARK_NOTIFICATION_READ = 'REQUEST_MARK_NOTIFICATION_READ';
export const REQUEST_MARK_NOTIFICATION_READ_SUCCESS = 'REQUEST_MARK_NOTIFICATION_READ_SUCCESS';
export const REQUEST_MARK_NOTIFICATION_READ_FAILURE = 'REQUEST_MARK_NOTIFICATION_READ_FAILURE';

// Refresh notifications (triggered by FCM)
export const ACTION_REFRESH_NOTIFICATIONS = 'ACTION_REFRESH_NOTIFICATIONS';

const defaultState = {
  notifications: [],
  unreadCount: 0,
  totalCount: 0,
  currentPage: 1,
  limit: 10,
  isLoading: false,
  isMarkingRead: false,
  error: null,
};

const reducer = createReducer(defaultState, {
  // Get Notifications
  [REQUEST_GET_NOTIFICATIONS]: (state) => ({
    ...state,
    isLoading: true,
    error: null,
  }),
  [REQUEST_GET_NOTIFICATIONS_SUCCESS]: (state, action) => {
    const response = action.payload || {};
    const data = response.data || {};
    const items = data.items || [];
    const total = data.total || 0;
    const unread = data.unread || 0;
    const page = action.meta?.variables?.page || 1;

    return {
      ...state,
      notifications: page === 1 ? items : [...state.notifications, ...items], // Append for pagination
      totalCount: total,
      unreadCount: unread, // Use unread count from API response
      currentPage: page,
      isLoading: false,
      error: null,
    };
  },
  [REQUEST_GET_NOTIFICATIONS_FAILURE]: (state, action) => ({
    ...state,
    isLoading: false,
    error: action.payload,
  }),

  // Mark as Read
  [REQUEST_MARK_NOTIFICATION_READ]: (state) => ({
    ...state,
    isMarkingRead: true,
    error: null,
  }),
  [REQUEST_MARK_NOTIFICATION_READ_SUCCESS]: (state, action) => {
    const { ids } = action.meta?.variables || {};
    const isMarkAll = ids && ids.includes('all');

    let updatedNotifications = [...state.notifications];
    let unreadCount = state.unreadCount;

    if (isMarkAll) {
      // Mark all as read
      updatedNotifications = updatedNotifications.map((notif) => ({
        ...notif,
        isRead: true,
        readAt: new Date().toISOString(),
      }));
      unreadCount = 0;
    } else if (ids && Array.isArray(ids)) {
      // Mark specific notifications as read
      updatedNotifications = updatedNotifications.map((notif) => {
        if (ids.includes(notif.id)) {
          if (!notif.isRead) {
            unreadCount = Math.max(0, unreadCount - 1);
          }
          return {
            ...notif,
            isRead: true,
            readAt: new Date().toISOString(),
          };
        }
        return notif;
      });
    }

    return {
      ...state,
      notifications: updatedNotifications,
      unreadCount,
      isMarkingRead: false,
      error: null,
    };
  },
  [REQUEST_MARK_NOTIFICATION_READ_FAILURE]: (state, action) => ({
    ...state,
    isMarkingRead: false,
    error: action.payload,
  }),

  // Refresh notifications (will trigger refetch)
  [ACTION_REFRESH_NOTIFICATIONS]: (state) => ({
    ...state,
    // Keep current state, just trigger refetch in saga
  }),
});

export default reducer;
export const namespace = 'userNotifications';

// Action Creators
export const getNotifications = (page = 1, limit = 10) => ({
  type: ACTION_GET_NOTIFICATIONS,
  payload: { page, limit },
});

export const markNotificationRead = (ids) => ({
  type: ACTION_MARK_NOTIFICATION_READ,
  payload: { ids },
});

export const refreshNotifications = () => ({
  type: ACTION_REFRESH_NOTIFICATIONS,
});

// API Functions
export const getNotificationsAPI =
  (page = 1, limit = 10) =>
  async (dispatch, getState) => {
    return await callAPI({
      method: 'get',
      apiPath: '/notification',
      actionTypes: [
        REQUEST_GET_NOTIFICATIONS,
        REQUEST_GET_NOTIFICATIONS_SUCCESS,
        REQUEST_GET_NOTIFICATIONS_FAILURE,
      ],
      variables: {
        page,
        limit,
      },
      dispatch,
      getState,
    });
  };

export const markNotificationReadAPI = (ids) => async (dispatch, getState) => {
  return await callAPI({
    method: 'put',
    apiPath: '/notification',
    actionTypes: [
      REQUEST_MARK_NOTIFICATION_READ,
      REQUEST_MARK_NOTIFICATION_READ_SUCCESS,
      REQUEST_MARK_NOTIFICATION_READ_FAILURE,
    ],
    variables: {
      ids,
      read: true,
    },
    dispatch,
    getState,
  });
};

// Selectors
export const selectNotifications = (state) => state.userNotifications?.notifications || [];
export const selectUnreadCount = (state) => state.userNotifications?.unreadCount || 0;
export const selectTotalCount = (state) => state.userNotifications?.totalCount || 0;
export const selectCurrentPage = (state) => state.userNotifications?.currentPage || 1;
export const selectLimit = (state) => state.userNotifications?.limit || 10;
export const selectHasMoreNotifications = (state) => {
  const total = state.userNotifications?.totalCount || 0;
  const current = state.userNotifications?.notifications?.length || 0;
  return current < total;
};
export const selectIsLoadingNotifications = (state) => state.userNotifications?.isLoading || false;
export const selectIsMarkingRead = (state) => state.userNotifications?.isMarkingRead || false;
export const selectNotificationsError = (state) => state.userNotifications?.error;
